import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FileUploadComplete } from 'node_js/models/business/lab/file-upload-complete';
import { FileUpload } from 'node_js/models/tables/file-upload.model';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { generateId, presentSnackBar, processImage } from 'src/global';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-photo-selector',
  templateUrl: './photo-selector.component.html',
  styleUrls: ['./photo-selector.component.scss'],
})
export class PhotoSelectorComponent implements OnInit {

  @Input() curatedImages: { url: string, name: string }[] = [];
  @Input() selectedCuratedImageUrl: string | null = null;
  @Input() showBackButton: boolean = true;
  @Input() backButtonCallback: (() => void) | null = null;
  @Input() existingFileUpload: FileUpload | null = null;
  @Input() entityId: string | null = null;
  @Input() tutorialTargetPrefix: string = 'create-lab';

  @Output() imageSelected: EventEmitter<{ url: string }> = new EventEmitter();
  @Output() imageUploaded: EventEmitter<FileUploadComplete> = new EventEmitter();

  @ViewChild('fileInput') fileInput!: ElementRef;

  iconFileUploadComplete: FileUploadComplete | null = null;

  // Default curated images list
  defaultCuratedImages: { url: string, name: string }[] = [
    { url: 'assets/photo-selection/01_laptop.svg', name: 'Laptop' },
    { url: 'assets/photo-selection/02_tablet.svg', name: 'Tablet' },
    { url: 'assets/photo-selection/03_smartphone.svg', name: 'Smartphone' },
    { url: 'assets/photo-selection/04_smartwatch.svg', name: 'Smartwatch' },
    { url: 'assets/photo-selection/05_vr_headset.svg', name: 'VR Headset' },
    { url: 'assets/photo-selection/06_ar_glasses.svg', name: 'AR Glasses' },
    { url: 'assets/photo-selection/07_robot.svg', name: 'Robot' },
    { url: 'assets/photo-selection/08_drone.svg', name: 'Drone' },
    { url: 'assets/photo-selection/09_3d_printer.svg', name: '3D Printer' },
    { url: 'assets/photo-selection/10_microscope.svg', name: 'Microscope' },
    { url: 'assets/photo-selection/11_telescope.svg', name: 'Telescope' },
    { url: 'assets/photo-selection/12_calculator.svg', name: 'Calculator' },
    { url: 'assets/photo-selection/13_lightbulb.svg', name: 'Lightbulb' },
    { url: 'assets/photo-selection/14_grad_cap.svg', name: 'Graduation Cap' },
    { url: 'assets/photo-selection/15_book_closed.svg', name: 'Book Closed' },
    { url: 'assets/photo-selection/16_book_open.svg', name: 'Book Open' },
    { url: 'assets/photo-selection/17_bookshelf.svg', name: 'Bookshelf' },
    { url: 'assets/photo-selection/18_chalkboard.svg', name: 'Chalkboard' },
    { url: 'assets/photo-selection/19_pencil.svg', name: 'Pencil' },
    { url: 'assets/photo-selection/20_pen.svg', name: 'Pen' },
    { url: 'assets/photo-selection/21_ruler.svg', name: 'Ruler' },
    { url: 'assets/photo-selection/22_compass_drafting.svg', name: 'Compass' },
    { url: 'assets/photo-selection/23_protractor.svg', name: 'Protractor' },
    { url: 'assets/photo-selection/24_backpack.svg', name: 'Backpack' },
    { url: 'assets/photo-selection/25_flask.svg', name: 'Flask' },
  ];

  constructor(
    private matDialog: MatDialog,
    private matSnackBar: MatSnackBar
  ) { }

  ngOnInit() {
    // Use default images if none provided
    if (this.curatedImages.length === 0) {
      this.curatedImages = this.defaultCuratedImages;
    }
  }

  selectCuratedImage(imageUrl: string) {
    // Clear uploaded image when selecting a curated image
    this.iconFileUploadComplete = null;
    this.selectedCuratedImageUrl = imageUrl;
    this.imageSelected.emit({ url: imageUrl });

    // Call back button callback if provided
    if (this.backButtonCallback) {
      this.backButtonCallback();
    }
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  async processFile(imageInput: any) {
    const file: File = imageInput.files[0];
    if (file) {
      try {
        let fileName = this.entityId ? `${this.entityId}-icon` : 'icon';
        let compressedFile: File = await processImage(file, fileName, this.matDialog, this.matSnackBar);

        // Use existing fileUpload if provided, otherwise create new one
        let fileUpload: FileUpload = this.existingFileUpload;
        if (fileUpload == null) {
          fileUpload = {
            id: generateId(),
            path: "",
            name: compressedFile.name,
            createdAt: new Date().toString(),
            filePathCode: "",
            clonedPath: ""
          }
        }

        // Generate a temporary URL for the image
        const imageUrl = URL.createObjectURL(compressedFile);

        this.iconFileUploadComplete = {
          file: compressedFile,
          fileUpload,
          srcUrl: imageUrl
        }

        // Emit the uploaded image
        this.imageUploaded.emit(this.iconFileUploadComplete);

        // Call back button callback if provided
        if (this.backButtonCallback) {
          this.backButtonCallback();
        }
      } catch (error) {
        console.error('Error processing image:', error);
        presentSnackBar("Failed to process image. Please try again.", SnackBarColor.DANGER, this.matSnackBar);
      }
    }
  }

  trackByImageUrl(index: number, image: { url: string }): string {
    return image.url;
  }

}
